import { useEffect, useRef, useState } from "react";
import { IoClose, IoSearch } from "react-icons/io5";
import LocationSearchPopover from "./LocationSearchPopover";
import { getListingDestinationLabel, getSearchDisplayLabel } from "@/utils/holidaySearch";
import styles from "./HolidayListing.module.css";

export default function DesktopListingSearch({
  urlParams,
  pageTitle,
  selection,
  draftLabel,
  months,
  selectedMonth,
  onMonthChange,
  onSearch,
  isSearching = false,
  locationOpen,
  onDestinationOpen,
  onDestinationQueryChange,
  onLocationClose,
  onLocationSelect,
  monthError = false,
}) {
  const destinationRef = useRef(null);
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const displayLabel = getListingDestinationLabel({
    selection,
    draftLabel,
    urlParams,
    pageTitle,
    fallback: "",
  });

  useEffect(() => {
    if (!isEditing) {
      setQuery(displayLabel);
    }
  }, [displayLabel, isEditing]);

  const getEditableQuery = () =>
    selection?.label || draftLabel || urlParams.cityName || urlParams.countryName || displayLabel;

  const handleFocus = () => {
    setIsEditing(true);
    const nextQuery = getEditableQuery();
    setQuery(nextQuery);
    onDestinationQueryChange(nextQuery);
    onDestinationOpen();
  };

  const handleChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    onDestinationQueryChange(value);
    if (!locationOpen) onDestinationOpen();
  };

  const handleClear = () => {
    setQuery("");
    onDestinationQueryChange("");
    inputRef.current?.focus();
    onDestinationOpen();
  };

  const handleClose = () => {
    setIsEditing(false);
    setQuery(displayLabel);
    onLocationClose();
  };

  const handleSelect = (item) => {
    setIsEditing(false);
    setQuery(getSearchDisplayLabel(item, item.label));
    onLocationSelect(item);
  };

  return (
    <div className={styles.desktopSearchBar}>
      <div className={styles.desktopSearchInner}>
        <div className={styles.desktopSearchFieldWrap} ref={destinationRef}>
          <label className={`${styles.desktopSearchField} ${locationOpen ? styles.desktopSearchFieldActive : ""}`}>
            <span className={styles.desktopSearchLabel}>Destination</span>
            <div className={styles.desktopSearchInputRow}>
              <input
                ref={inputRef}
                type="text"
                className={styles.desktopSearchInput}
                value={query}
                onChange={handleChange}
                onFocus={handleFocus}
                placeholder="Search countries, cities..."
                autoComplete="off"
                aria-expanded={locationOpen}
                aria-haspopup="listbox"
              />
              {query ? (
                <button
                  type="button"
                  className={styles.desktopSearchClearBtn}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={handleClear}
                  aria-label="Clear destination"
                >
                  <IoClose />
                </button>
              ) : null}
            </div>
          </label>
          {locationOpen ? (
            <LocationSearchPopover
              anchorRef={destinationRef}
              query={query}
              onClose={handleClose}
              onSelect={handleSelect}
            />
          ) : null}
        </div>

        <span className={styles.desktopSearchDivider} aria-hidden="true" />

        <label
          className={`${styles.desktopSearchField} ${styles.desktopSearchMonthField} ${
            monthError ? styles.desktopSearchFieldError : ""
          }`.trim()}
        >
          <span className={styles.desktopSearchLabel}>Select Month</span>
          <select
            className={styles.desktopSearchSelect}
            value={selectedMonth}
            onChange={(event) => onMonthChange(event.target.value)}
            disabled={!months.length}
            aria-invalid={monthError}
          >
            <option value="">Select month</option>
            {months.map((month) => (
              <option key={month.id} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className={styles.desktopSearchBtn}
          onClick={onSearch}
          disabled={isSearching}
          aria-label="Search"
        >
          {isSearching ? (
            <span className={styles.searchBtnSpinner} aria-hidden="true" />
          ) : (
            <IoSearch />
          )}
        </button>
      </div>
    </div>
  );
}
